import { useQuery } from '@tanstack/react-query';

import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

export interface ContactDetail {
  id: number;
  contact_id: number;
  type: string; // 'email' or 'phone'
  value: string;
  label?: string;
  is_primary: boolean;
  created_at: string;
}

export interface ContactAssociation {
  id: number;
  contact_id: number;
  location_id?: number;
  building_id?: number;
  unit_id?: number;
  created_at: string;
  location?: {
    id: number;
    name: string;
  };
  building?: {
    id: number;
    name: string;
  };
  unit?: {
    id: number;
    name: string;
  };
}

export interface Contact {
  id: number;
  name: string;
  user_id?: string;
  role: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  details: ContactDetail[];
  associations: ContactAssociation[];
}

export type ApiResponse_GetAll_Contact = Contact[];

export type ApiRequest_GetAll_Contact = {
  location_id: number;
};

const ENDPOINT = 'contacts';
const METHOD = 'GET';

export const QUERY_KEY = (locationId?: number) => ['contacts', 'all', locationId];

export const useGetAllContacts = (enabled = true) => {
  const { session } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();

  return useQuery({
    queryKey: QUERY_KEY(selectedPropertyLocation?.id),
    queryFn: async (): Promise<ApiResponse_GetAll_Contact> => {
      if (!selectedPropertyLocation?.id) {
        throw new Error('No location selected');
      }

      return handleApiCall<ApiResponse_GetAll_Contact, ApiRequest_GetAll_Contact>(
        session,
        METHOD,
        ENDPOINT,
        {
          location_id: selectedPropertyLocation.id,
        }
      );
    },
    enabled: enabled && !!selectedPropertyLocation?.id && !!session,
  });
};
